import { ChangeDetectionStrategy, Component, computed, input } from "@angular/core";
import { IIcon, IIconComponent } from "@front/m19-ui";
import { cva } from "class-variance-authority";

export type OverlayVariant = "warning" | "promo" | "info";

const cardVariants = cva("relative max-w-full min-w-128 overflow-hidden rounded-3xl border bg-white", {
  variants: {
    variant: {
      warning: "border-warning-border",
      promo: "border-success-border",
      info: "border-info-border",
    },
  },
});

const contentVariants = cva("relative flex flex-col gap-3 px-14 py-12 pr-44", {
  variants: {
    variant: {
      warning: "bg-linear-to-r from-white/20 from-45% to-warning/4",
      promo: "bg-linear-to-r from-transparent to-main/5 to-80%",
      info: "",
    },
  },
});

const iconBoxVariants = cva("flex size-10 shrink-0 items-center justify-center rounded-xl border", {
  variants: {
    variant: {
      warning: "border-warning-border bg-warning-background text-warning",
      promo: "",
      info: "border-info-border bg-white text-info",
    },
  },
});

@Component({
  selector: "app-overlay-layout",
  imports: [IIconComponent],
  host: { class: "flex justify-center pt-[10vh]" },
  template: `
    <div [class]="cardClass()">
      <div
        class="absolute inset-0 bg-[radial-gradient(var(--color-border-base)_1px,transparent_1px)] bg-size-[14px_14px]"
      ></div>
      <div class="absolute inset-0 bg-linear-to-l from-transparent from-35% to-white"></div>
      <div [class]="contentClass()">
        @if (variant() === "promo") {
          <div class="flex flex-col">
            <h3 data-testid="overlay-title" class="leading-7">{{ title() }}</h3>
            @if (subtitle(); as subtitle) {
              <p data-testid="overlay-subtitle" class="text-muted-foreground text-xl font-semibold">
                {{ subtitle }}
              </p>
            }
          </div>
        } @else {
          <div class="flex items-center gap-5">
            <div data-testid="overlay-icon" [class]="iconBoxClass()">
              <IIcon class="text-xl" [name]="icon()" />
            </div>
            <h3 data-testid="overlay-title">{{ title() }}</h3>
          </div>
        }
        <div class="text-foreground type-body flex flex-col gap-4 py-5">
          <ng-content select="[overlayBody]" />
        </div>
        <div class="flex items-center gap-3">
          <ng-content select="[overlayCta]" />
        </div>
      </div>
    </div>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OverlayLayoutComponent {
  title = input.required<string>();
  subtitle = input<string>();
  variant = input<OverlayVariant>("info");
  icon = input<IIcon>("infoCircle");

  protected readonly cardClass = computed(() => cardVariants({ variant: this.variant() }));
  protected readonly contentClass = computed(() => contentVariants({ variant: this.variant() }));
  protected readonly iconBoxClass = computed(() => iconBoxVariants({ variant: this.variant() }));
}
